import { roadMapBridge } from '../render/roadMapBridge';

// cityPath — how an artist gets from one place to another on foot.
//
// The street already knows where its pavements are: render/roadMap photographs
// the city from above and classifies every texel as road, pavement or covered
// (buildings and props). This reads that map as a walking grid — pavement is
// easy, a road is crossable but avoided, anything covered is a wall — and finds
// the way with A*. Same map, same search, same tie-breaks: every viewer walks
// the artist down the same pavement.
//
// ⚠ IT IS AN ESTIMATE OF A PATH, NOT A TIME. The server decided when they leave
// and when they arrive (API city/cityTimeline); the walk is fitted to that
// window. A path that is long for its window is simply walked a little faster.
//
// Until the road map exists (the first seconds of a visit), a walk is a straight
// line; the next lookup after the map lands gets the real path.

const CELL = 2;              // texels per cell (≈0.47 units): fine enough for a pavement
const ROAD_COST = 4.0;       // crossing is allowed, strolling down the middle is not
const NEAR_WALL_COST = 1.6;  // keep a little off the walls
const COURT_COST = 1.4;      // a court is open ground, a little less inviting than pavement
const MAX_EXPANSIONS = 160000;

let grid = null;             // { w, h, cost: Float32Array, ox, oz, cell, map }
let gridFor = null;

function buildGrid(map) {
  const { cls, surf, w: W, h: H, rect, dBlock } = map;
  const u = map.unitsPerTexel || rect.w / W;
  const gw = Math.ceil(W / CELL); const gh = Math.ceil(H / CELL);
  const cost = new Float32Array(gw * gh);
  const wallTex = 0.35 / u;   // within 0.35 units of a wall
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      let blocked = false; let road = 0; let court = 0; let n = 0; let nearWall = false;
      for (let dy = 0; dy < CELL; dy++) {
        const y = gy * CELL + dy; if (y >= H) continue;
        for (let dx = 0; dx < CELL; dx++) {
          const x = gx * CELL + dx; if (x >= W) continue;
          const i = y * W + x; const c = cls[i];
          n++;
          if (c === 3 || c === 0) blocked = true;
          // The surface layer, when the road map has one, says asphalt and court
          // from the ground's colour; the lamp classes are the fallback.
          else if (surf ? surf[i] === 1 : c === 1) road++;
          else if (surf && surf[i] === 3) court++;
          if (dBlock && dBlock[i] < wallTex) nearWall = true;
        }
      }
      let k = Infinity;
      if (!blocked && n) {
        k = road * 2 > n ? ROAD_COST : court * 2 > n ? COURT_COST : 1;
        k += nearWall ? NEAR_WALL_COST : 0;
      }
      cost[gy * gw + gx] = k;
    }
  }
  return { w: gw, h: gh, cost, minX: rect.minX, minZ: rect.minZ, cu: u * CELL, map };
}

function getGrid() {
  const map = roadMapBridge.map;
  if (!map) return null;
  if (gridFor !== map) {
    const t0 = performance.now();
    grid = buildGrid(map);
    gridFor = map;
    cache.clear();
    console.log(`[city] walking grid ${grid.w}x${grid.h} in ${(performance.now() - t0).toFixed(0)}ms`);
  }
  return grid;
}

const toCell = (g, x, z) => [Math.floor((x - g.minX) / g.cu), Math.floor((z - g.minZ) / g.cu)];
const toWorld = (g, cx, cy) => [g.minX + (cx + 0.5) * g.cu, g.minZ + (cy + 0.5) * g.cu];
const inside = (g, cx, cy) => cx >= 0 && cy >= 0 && cx < g.w && cy < g.h;

/**
 * The nearest place you can STAND, at the photo's own resolution (≈0.23 units):
 * pavement, a lot or a court, with its neighbours standable too so nobody is
 * balanced on a kerb. Narrow pavements are about two of these wide, which is why
 * this does not use the walking grid's coarser cells.
 */
function snapStanding(x, z, radius) {
  const map = roadMapBridge.map;
  const surf = map?.surf;
  if (!surf) return null;
  const { w: W, h: H, rect } = map;
  const u = map.unitsPerTexel || rect.w / W;
  const ci = Math.floor((x - rect.minX) / u); const cr = Math.floor((z - rect.minZ) / u);
  const good = (i, r) => {
    if (i < 1 || r < 1 || i >= W - 1 || r >= H - 1) return false;
    for (let dr = -1; dr <= 1; dr++) for (let di = -1; di <= 1; di++) {
      const s = surf[(r + dr) * W + i + di];
      if (s !== 2 && s !== 3) return false;
    }
    return true;
  };
  if (good(ci, cr)) return [x, z];
  const R = Math.ceil(radius / u);
  let best = null; let bd = Infinity;
  for (let r = 1; r <= R; r++) {
    for (let j = cr - r; j <= cr + r; j++) {
      for (let i = ci - r; i <= ci + r; i++) {
        if (Math.max(Math.abs(i - ci), Math.abs(j - cr)) !== r || !good(i, j)) continue;
        const d = (i - ci) ** 2 + (j - cr) ** 2;
        if (d < bd) { bd = d; best = [i, j]; }
      }
    }
    if (best) break;
  }
  return best ? [rect.minX + (best[0] + 0.5) * u, rect.minZ + (best[1] + 0.5) * u] : null;
}

/** The nearest cell you can stand in (spiral search), as world [x, z] — or the input. */
export function snapWalkable(x, z, { radius = 4, avoidRoad = true } = {}) {
  if (avoidRoad) { const s = snapStanding(x, z, radius); if (s) return s; }
  const g = getGrid();
  if (!g) return [x, z];
  const [cx, cy] = toCell(g, x, z);
  // Standing ground: pavement, a lot or a court — never asphalt when avoidRoad.
  const ok = (i, j) => inside(g, i, j) && Number.isFinite(g.cost[j * g.w + i]) && (!avoidRoad || g.cost[j * g.w + i] < ROAD_COST);
  if (ok(cx, cy)) return [x, z];
  const R = Math.ceil(radius / g.cu);
  let best = null; let bd = Infinity;
  for (let r = 1; r <= R; r++) {
    for (let j = cy - r; j <= cy + r; j++) {
      for (let i = cx - r; i <= cx + r; i++) {
        if (Math.max(Math.abs(i - cx), Math.abs(j - cy)) !== r || !ok(i, j)) continue;
        const d = (i - cx) ** 2 + (j - cy) ** 2;
        if (d < bd) { bd = d; best = [i, j]; }
      }
    }
    if (best) break;
  }
  return best ? toWorld(g, best[0], best[1]) : [x, z];
}

// ── Coming out of a building ────────────────────────────────────────────────
// Leaving a venue used to put you at its building's centre plus a fixed (+6, +6)
// on the map, whatever was there — out of the concert hall that was inside
// another building, sealed in (Khari, 2026-10-23). The way out is now the
// nearest ground you can stand on that the STREET reaches: flood-filled from the
// city's own spawn, so a pocket closed in by walls never counts.
let reach = null;            // { g, key, mark: Uint8Array } — cells reachable from home

function reachableFrom(g, hx, hz) {
  const key = `${hx.toFixed(1)},${hz.toFixed(1)}`;
  if (reach && reach.g === g && reach.key === key) return reach.mark;
  const mark = new Uint8Array(g.w * g.h);
  const open = (i) => Number.isFinite(g.cost[i]);
  // Start from the nearest open cell to home.
  let [sx, sy] = toCell(g, hx, hz);
  if (!inside(g, sx, sy) || !open(sy * g.w + sx)) {
    let found = null;
    for (let r = 1; r < 40 && !found; r++) {
      for (let j = sy - r; j <= sy + r && !found; j++) for (let i = sx - r; i <= sx + r; i++) {
        if (inside(g, i, j) && open(j * g.w + i)) { found = [i, j]; break; }
      }
    }
    if (!found) return null;
    [sx, sy] = found;
  }
  const q = new Int32Array(g.w * g.h); let head = 0; let tail = 0;
  q[tail++] = sy * g.w + sx; mark[sy * g.w + sx] = 1;
  while (head < tail) {
    const c = q[head++]; const x = c % g.w; const y = (c - x) / g.w;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx; const ny = y + dy;
      if (!inside(g, nx, ny)) continue;
      const n = ny * g.w + nx;
      if (mark[n] || !open(n)) continue;
      mark[n] = 1; q[tail++] = n;
    }
  }
  reach = { g, key, mark };
  return mark;
}

/**
 * Where to stand on coming out of a building at `toward` [x, z]: the nearest
 * reachable pavement (a road only if there is nothing else), preferring a spot
 * a little off the wall. `home` [x, z] is a point surely on the street (the
 * city's spawn). Null until the road map exists.
 */
export function exitSpot(toward, home, { radius = 16 } = {}) {
  const g = getGrid();
  if (!g) return null;
  const mark = reachableFrom(g, home[0], home[1]);
  if (!mark) return null;
  const [cx, cy] = toCell(g, toward[0], toward[1]);
  const R = Math.ceil(radius / g.cu);
  const pick = (ok) => {
    for (let r = 1; r <= R; r++) {
      let best = null; let bd = Infinity;
      for (let j = cy - r; j <= cy + r; j++) {
        for (let i = cx - r; i <= cx + r; i++) {
          if (Math.max(Math.abs(i - cx), Math.abs(j - cy)) !== r || !inside(g, i, j)) continue;
          const n = j * g.w + i;
          if (!mark[n] || !ok(g.cost[n], n)) continue;
          const d = (i - cx) ** 2 + (j - cy) ** 2;
          if (d < bd) { bd = d; best = [i, j]; }
        }
      }
      if (best) return best;
    }
    return null;
  };
  // Clear of walls, so you come out looking AT the building rather than with
  // your nose on a shop window: pavement with room first, then the road's edge
  // with room (pavements are often narrower than that), then any pavement.
  const map = g.map; const tu = map.unitsPerTexel || map.rect.w / map.w;
  const clearAt = (i) => {
    if (!map.dBlock) return Infinity;
    const x = i % g.w; const y = (i - x) / g.w;
    const tx = Math.min(map.w - 1, x * CELL + 1); const ty = Math.min(map.h - 1, y * CELL + 1);
    return map.dBlock[ty * map.w + tx] * tu;
  };
  const cell = pick((k, n) => clearAt(n) >= EXIT_VIEW)                       // back far enough to see its front
    || pick((k, n) => k < ROAD_COST && clearAt(n) >= EXIT_CLEAR) || pick((k, n) => clearAt(n) >= EXIT_CLEAR)
    || pick((k) => k < ROAD_COST) || pick((k) => Number.isFinite(k));
  if (!cell) return null;
  return toWorld(g, cell[0], cell[1]);
}
const EXIT_VIEW = 2.2;       // units from the nearest wall (~5 m): the building's front in view
const EXIT_CLEAR = 1.2;      // at least this (~2.8 m)

// ── A* on the cost grid ─────────────────────────────────────────────────────
function astar(g, sx, sy, tx, ty) {
  const N = g.w * g.h;
  const gScore = new Float32Array(N).fill(Infinity);
  const came = new Int32Array(N).fill(-1);
  const closed = new Uint8Array(N);
  // Binary heap of [f, idx].
  const heap = []; const push = (f, i) => {
    heap.push([f, i]); let k = heap.length - 1;
    while (k > 0) { const p = (k - 1) >> 1; if (heap[p][0] < heap[k][0] || (heap[p][0] === heap[k][0] && heap[p][1] <= heap[k][1])) break; [heap[p], heap[k]] = [heap[k], heap[p]]; k = p; }
  };
  const pop = () => {
    const top = heap[0]; const last = heap.pop();
    if (heap.length) {
      heap[0] = last; let k = 0;
      for (;;) {
        const l = 2 * k + 1; const r = l + 1; let m = k;
        const less = (a, b) => heap[a][0] < heap[b][0] || (heap[a][0] === heap[b][0] && heap[a][1] < heap[b][1]);
        if (l < heap.length && less(l, m)) m = l;
        if (r < heap.length && less(r, m)) m = r;
        if (m === k) break;
        [heap[m], heap[k]] = [heap[k], heap[m]]; k = m;
      }
    }
    return top;
  };
  const h = (x, y) => { const dx = Math.abs(x - tx); const dy = Math.abs(y - ty); return (dx + dy) + (Math.SQRT2 - 2) * Math.min(dx, dy); };
  const s = sy * g.w + sx; const t = ty * g.w + tx;
  gScore[s] = 0; push(h(sx, sy), s);
  let expansions = 0;
  const D = [[1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1], [1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [-1, -1, Math.SQRT2]];
  while (heap.length) {
    const [, cur] = pop();
    if (cur === t) break;
    if (closed[cur]) continue;
    closed[cur] = 1;
    if (++expansions > MAX_EXPANSIONS) return null;
    const cx = cur % g.w; const cy = (cur - cx) / g.w;
    for (const [dx, dy, step] of D) {
      const nx = cx + dx; const ny = cy + dy;
      if (!inside(g, nx, ny)) continue;
      const ni = ny * g.w + nx;
      const c = g.cost[ni];
      if (!Number.isFinite(c) || closed[ni]) continue;
      // No corner cutting through a wall on a diagonal.
      if (dx && dy && (!Number.isFinite(g.cost[cy * g.w + nx]) || !Number.isFinite(g.cost[ny * g.w + cx]))) continue;
      const ng = gScore[cur] + step * c;
      if (ng < gScore[ni]) { gScore[ni] = ng; came[ni] = cur; push(ng + h(nx, ny), ni); }
    }
  }
  if (came[t] === -1 && t !== s) return null;
  const out = [];
  for (let k = t; k !== -1; k = came[k]) out.push(k);
  return out.reverse();
}

// Straight enough? Walk the line between two cells: no walls, and no road the
// original route did not already cross.
function clearLine(g, a, b, allowRoad) {
  const [ax, ay] = [a % g.w, (a - (a % g.w)) / g.w];
  const [bx, by] = [b % g.w, (b - (b % g.w)) / g.w];
  const n = Math.ceil(Math.hypot(bx - ax, by - ay) * 2);
  for (let i = 1; i < n; i++) {
    const x = Math.round(ax + ((bx - ax) * i) / n); const y = Math.round(ay + ((by - ay) * i) / n);
    const c = g.cost[y * g.w + x];
    if (!Number.isFinite(c)) return false;
    if (!allowRoad && c >= ROAD_COST) return false;
  }
  return true;
}

function smooth(g, cells) {
  if (cells.length < 3) return cells;
  const roadUpTo = new Uint8Array(cells.length);
  for (let i = 0; i < cells.length; i++) roadUpTo[i] = g.cost[cells[i]] >= ROAD_COST ? 1 : 0;
  const out = [cells[0]];
  let i = 0;
  while (i < cells.length - 1) {
    let j = cells.length - 1;
    for (; j > i + 1; j--) {
      let crossed = false;
      for (let k = i; k <= j; k++) if (roadUpTo[k]) { crossed = true; break; }
      if (clearLine(g, cells[i], cells[j], crossed)) break;
    }
    out.push(cells[j]);
    i = j;
  }
  return out;
}

const cache = new Map();

/**
 * A walking route from a to b ([x, z] each): [[x, z], …] with a and b at the
 * ends, plus its length. Cached; falls back to the straight line.
 */
export function routeBetween(a, b) {
  const key = `${a[0].toFixed(1)},${a[1].toFixed(1)}>${b[0].toFixed(1)},${b[1].toFixed(1)}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const g = getGrid();
  let pts = null;
  if (g) {
    const sa = snapWalkable(a[0], a[1], { avoidRoad: false });
    const sb = snapWalkable(b[0], b[1], { avoidRoad: false });
    const [sx, sy] = toCell(g, sa[0], sa[1]);
    const [tx, ty] = toCell(g, sb[0], sb[1]);
    if (inside(g, sx, sy) && inside(g, tx, ty)) {
      const cells = astar(g, sx, sy, tx, ty);
      if (cells) pts = [a, ...smooth(g, cells).slice(1, -1).map((c) => toWorld(g, c % g.w, (c - (c % g.w)) / g.w)), b];
    }
  }
  if (!pts) pts = [a, b];
  let len = 0;
  const cum = [0];
  for (let i = 1; i < pts.length; i++) { len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]); cum.push(len); }
  const route = { pts, cum, len, real: !!g };
  // A straight-line fallback is not cached past the map's arrival.
  if (g) cache.set(key, route);
  if (cache.size > 600) cache.delete(cache.keys().next().value);
  return route;
}

/** Point and heading at a distance along a route. */
export function along(route, d) {
  const { pts, cum, len } = route;
  if (pts.length < 2 || len <= 0) return { x: pts[0][0], z: pts[0][1], heading: 0 };
  const dd = Math.max(0, Math.min(len, d));
  let i = 1;
  while (i < cum.length - 1 && cum[i] < dd) i++;
  const a = pts[i - 1]; const b = pts[i];
  const seg = cum[i] - cum[i - 1] || 1;
  const f = (dd - cum[i - 1]) / seg;
  return { x: a[0] + (b[0] - a[0]) * f, z: a[1] + (b[1] - a[1]) * f, heading: Math.atan2(b[0] - a[0], b[1] - a[1]) };
}

/** Is the walking grid ready (the street's road map has been read)? */
export function pathsReady() { return !!getGrid(); }
